import React from "react";
import { createPortal } from "react-dom";
import { FONT } from "../constants";
import { C } from "../styles";

export type DrawerCategory = "earning" | "deduction";
export type DrawerMode = "add" | "edit";

export interface Draft {
  name?: string;
  description?: string;
  calc_method?: string;
  default_rate?: string;
  default_amount?: string;
  unit_label?: string;
  federal_taxable?: boolean;
  cpp_contributable?: boolean;
  ei_insurable?: boolean;
  vacationable?: boolean;
  wcb_reportable?: boolean;
  t4_box?: string;
  is_pre_tax?: boolean;
  employer_matched?: boolean;
}

interface Props {
  open: boolean;
  mode: DrawerMode;
  category: DrawerCategory;
  draft: Draft;
  setDraft: (d: Draft) => void;
  setCategory: (c: DrawerCategory) => void;
  editingIsDefault: boolean;
  saving: boolean;
  deleting: boolean;
  saveError: string | null;
  onClose: () => void;
  onSave: () => void;
  onRequestDelete: () => void;
}

const earningFlags = [
  { key: "federal_taxable", name: "Federal taxable", desc: "Counts toward federal and provincial income tax" },
  { key: "cpp_contributable", name: "CPP contributable", desc: "Counts toward Canada Pension Plan contributions" },
  { key: "ei_insurable", name: "EI insurable", desc: "Counts toward Employment Insurance premiums" },
  { key: "vacationable", name: "Vacationable", desc: "Accrues vacation pay on this earning" },
  { key: "wcb_reportable", name: "WCB reportable", desc: "Counts toward Workers Compensation Board reporting" },
];

const deductionFlags = [
  { key: "is_pre_tax", name: "Pre-tax", desc: "Reduces the employee's taxable income before tax is calculated" },
  { key: "employer_matched", name: "Employer matched", desc: "The company contributes an equivalent or matching amount" },
];

export default function PayTypeDrawer({
  open, mode, category, draft, setDraft, setCategory,
  editingIsDefault, saving, deleting, saveError,
  onClose, onSave, onRequestDelete,
}: Props) {
  if (!open) return null;

  const calcOptions = category === "earning"
    ? [
        { key: "fixed", name: "Fixed amount", formula: "amount per pay period" },
        { key: "rate_hours", name: "Rate x hours", formula: "hourly_rate x hours_worked" },
        { key: "rate_units", name: "Rate x units", formula: "unit_rate x quantity (visits, km, etc.)" },
        { key: "percent_gross", name: "Percentage of gross", formula: "percentage x gross_pay" },
      ]
    : [
        { key: "fixed", name: "Fixed amount", formula: "amount per pay period" },
        { key: "percent_gross", name: "Percentage of gross", formula: "percentage x gross_pay" },
      ];

  const showRateFields = draft.calc_method === "rate_hours" || draft.calc_method === "rate_units" || draft.calc_method === "percent_gross" || (draft.calc_method === "fixed" && category === "deduction");

  return createPortal(
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(10,26,30,.42)", zIndex: 1500, display: "flex", justifyContent: "flex-end" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 560, height: "100vh", background: "#fff", display: "flex", flexDirection: "column", boxShadow: "-20px 0 60px rgba(10,26,30,.18)", overflow: "hidden", fontFamily: FONT }}>

        <div style={{ padding: "24px 32px 20px", borderBottom: "1px solid " + C.line, flex: "0 0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <div style={{ fontSize: 18, fontWeight: 600, color: C.ink, letterSpacing: "-0.015em" }}>
              {mode === "add" ? "New" : "Edit"} {category === "earning" ? "earning" : "deduction"}
            </div>
            <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: 6, border: 0, background: "transparent", color: C.ink, cursor: "pointer", display: "grid", placeItems: "center", fontFamily: FONT }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </button>
          </div>
          <div style={{ fontSize: 13, color: C.ink, lineHeight: 1.55 }}>
            Configure how this item is calculated. Tax treatment options coming next.
          </div>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "24px 32px", display: "flex", flexDirection: "column", gap: 24 }}>

          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: C.ink, letterSpacing: "0.06em", textTransform: "uppercase", paddingBottom: 8, borderBottom: "1px solid " + (C as any).lineSoft }}>Item type</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div onClick={() => mode === "add" && setCategory("earning")} style={{ padding: "14px 16px", border: "1.5px solid " + (category === "earning" ? C.ink : C.line), borderRadius: 8, cursor: mode === "add" ? "pointer" : "not-allowed", display: "flex", alignItems: "center", gap: 12, background: category === "earning" ? "#FAFBFC" : "#fff", opacity: mode === "add" ? 1 : 0.6 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: "#0D8050", flex: "0 0 10px" }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: C.ink, marginBottom: 2 }}>Earning</div>
                  <div style={{ fontSize: 11.5, color: C.ink, lineHeight: 1.45 }}>Money paid to employees</div>
                </div>
              </div>
              <div onClick={() => mode === "add" && setCategory("deduction")} style={{ padding: "14px 16px", border: "1.5px solid " + (category === "deduction" ? C.ink : C.line), borderRadius: 8, cursor: mode === "add" ? "pointer" : "not-allowed", display: "flex", alignItems: "center", gap: 12, background: category === "deduction" ? "#FAFBFC" : "#fff", opacity: mode === "add" ? 1 : 0.6 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: "#9C5A0F", flex: "0 0 10px" }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: C.ink, marginBottom: 2 }}>Deduction</div>
                  <div style={{ fontSize: 11.5, color: C.ink, lineHeight: 1.45 }}>Money taken from paycheck</div>
                </div>
              </div>
            </div>
            {mode === "edit" && <div style={{ fontSize: 11.5, color: C.ink, fontStyle: "italic" }}>Item type cannot be changed after creation.</div>}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: C.ink, letterSpacing: "0.06em", textTransform: "uppercase", paddingBottom: 8, borderBottom: "1px solid " + (C as any).lineSoft }}>Details</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={{ fontSize: 11.5, fontWeight: 600, color: C.ink }}>Name</label>
              <input type="text" value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder={category === "earning" ? "e.g., Care visit bonus" : "e.g., Parking fee"}
                style={{ padding: "10px 12px", border: "1px solid " + C.line, borderRadius: 6, fontFamily: FONT, fontSize: 13.5, color: C.ink, background: "#fff", outline: "none", width: "100%" }} />
              <span style={{ fontSize: 11.5, color: C.ink, marginTop: 2 }}>Appears on pay stubs and pay runs</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={{ fontSize: 11.5, fontWeight: 600, color: C.ink }}>Description (optional)</label>
              <input type="text" value={draft.description || ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                placeholder="Brief internal description"
                style={{ padding: "10px 12px", border: "1px solid " + C.line, borderRadius: 6, fontFamily: FONT, fontSize: 13.5, color: C.ink, background: "#fff", outline: "none", width: "100%" }} />
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: C.ink, letterSpacing: "0.06em", textTransform: "uppercase", paddingBottom: 8, borderBottom: "1px solid " + (C as any).lineSoft }}>Calculation method</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {calcOptions.map((opt) => {
                const isActive = draft.calc_method === opt.key;
                return (
                  <div key={opt.key} onClick={() => setDraft({ ...draft, calc_method: opt.key })} style={{ padding: "12px 14px", border: "1px solid " + (isActive ? C.ink : C.line), borderRadius: 7, cursor: "pointer", display: "flex", alignItems: "center", gap: 11, background: isActive ? "#F7F9FB" : "#fff" }}>
                    <div style={{ width: 16, height: 16, borderRadius: "50%", border: "1.5px solid " + (isActive ? C.ink : "#C2CCD8"), flex: "0 0 16px", position: "relative" }}>
                      {isActive && <div style={{ position: "absolute", inset: 3, background: C.ink, borderRadius: "50%" }} />}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: C.ink, marginBottom: 1 }}>{opt.name}</div>
                      <div style={{ fontSize: 11.5, color: C.ink, fontFamily: "'JetBrains Mono', monospace" }}>{opt.formula}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {showRateFields && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 6 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <label style={{ fontSize: 11.5, fontWeight: 600, color: C.ink }}>
                    {draft.calc_method === "percent_gross" ? "Default percentage" : "Default rate"}
                  </label>
                  <div style={{ position: "relative" }}>
                    {draft.calc_method !== "percent_gross" && <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: C.ink, fontSize: 13.5, pointerEvents: "none", fontFamily: "'JetBrains Mono', monospace" }}>$</span>}
                    <input type="text" inputMode="decimal" value={category === "earning" ? (draft.default_rate || "") : (draft.default_amount || "")}
                      onChange={(e) => setDraft(category === "earning" ? { ...draft, default_rate: e.target.value } : { ...draft, default_amount: e.target.value })}
                      placeholder={draft.calc_method === "percent_gross" ? "5.00" : "25.00"}
                      style={{ padding: draft.calc_method === "percent_gross" ? "10px 12px" : "10px 12px 10px 26px", border: "1px solid " + C.line, borderRadius: 6, fontFamily: "'JetBrains Mono', monospace", fontSize: 13.5, color: C.ink, background: "#fff", outline: "none", width: "100%" }} />
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <label style={{ fontSize: 11.5, fontWeight: 600, color: C.ink }}>Unit label</label>
                  <input type="text" value={draft.unit_label || ""} onChange={(e) => setDraft({ ...draft, unit_label: e.target.value })}
                    placeholder={draft.calc_method === "percent_gross" ? "% of gross" : (draft.calc_method === "rate_hours" ? "per hour" : "per visit, per km")}
                    style={{ padding: "10px 12px", border: "1px solid " + C.line, borderRadius: 6, fontFamily: FONT, fontSize: 13.5, color: C.ink, background: "#fff", outline: "none", width: "100%" }} />
                </div>
              </div>
            )}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: C.ink, letterSpacing: "0.06em", textTransform: "uppercase", paddingBottom: 8, borderBottom: "1px solid " + (C as any).lineSoft }}>Tax treatment &middot; Canada</div>
            <div style={{ padding: "12px 14px", background: C.tealSoft, borderLeft: "2px solid " + C.teal, borderRadius: "0 6px 6px 0", fontSize: 12, color: C.tealInk, lineHeight: 1.55, display: "flex", gap: 10, alignItems: "flex-start" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ flex: "0 0 14px", marginTop: 1 }}><circle cx="12" cy="12" r="9"/><path d="M12 16v-4M12 8h.01"/></svg>
              <span>{category === "earning"
                ? "These flags determine how Novala calculates federal tax, CPP, EI, and vacation pay for this item."
                : "Pre-tax deductions reduce the employee's taxable income. Employer-matched means the company contributes too."}</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 0, border: "1px solid " + C.line, borderRadius: 8, overflow: "hidden", background: "#fff" }}>
              {(category === "earning" ? earningFlags : deductionFlags).map((flag, idx, arr) => {
                const isOn = !!(draft as any)[flag.key];
                return (
                  <div key={flag.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 14px", borderBottom: idx === arr.length - 1 ? "none" : "1px solid " + (C as any).lineSoft, gap: 14 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: C.ink, marginBottom: 1 }}>{flag.name}</div>
                      <div style={{ fontSize: 11.5, color: C.ink, lineHeight: 1.45 }}>{flag.desc}</div>
                    </div>
                    <div onClick={() => setDraft({ ...draft, [flag.key]: !isOn })} style={{ position: "relative", width: 38, height: 22, flex: "0 0 38px", cursor: "pointer" }}>
                      <div style={{ position: "absolute", inset: 0, background: isOn ? C.teal : "#D7DCE3", borderRadius: 11, transition: "background .18s" }} />
                      <div style={{ position: "absolute", height: 16, width: 16, left: isOn ? 19 : 3, bottom: 3, background: "#fff", borderRadius: "50%", transition: "left .18s", boxShadow: "0 1px 2px rgba(0,0,0,.15)" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {category === "earning" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: C.ink, letterSpacing: "0.06em", textTransform: "uppercase", paddingBottom: 8, borderBottom: "1px solid " + (C as any).lineSoft }}>Reporting</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={{ fontSize: 11.5, fontWeight: 600, color: C.ink }}>T4 box (year-end reporting)</label>
                <select value={draft.t4_box || "14"} onChange={(e) => setDraft({ ...draft, t4_box: e.target.value })}
                  style={{ padding: "10px 12px", border: "1px solid " + C.line, borderRadius: 6, fontFamily: FONT, fontSize: 13.5, color: C.ink, background: "#fff", outline: "none", width: "100%", cursor: "pointer" }}>
                  <option value="14">Box 14 - Employment income (default)</option>
                  <option value="24">Box 24 - EI insurable earnings</option>
                  <option value="26">Box 26 - CPP pensionable earnings</option>
                  <option value="40">Box 40 - Other taxable allowances and benefits</option>
                  <option value="custom">Custom box</option>
                </select>
                <span style={{ fontSize: 11.5, color: C.ink, marginTop: 2 }}>Used when generating T4 slips at year-end</span>
              </div>
            </div>
          )}

          {saveError && (
            <div style={{ padding: "10px 12px", background: "#FBEDEC", border: "1px solid #F0C3BC", borderRadius: 6, fontSize: 12.5, color: "#B53B2E" }}>
              {saveError}
            </div>
          )}
        </div>

        <div style={{ padding: "18px 32px", borderTop: "1px solid " + C.line, background: "#fff", display: "flex", alignItems: "center", gap: 10, flex: "0 0 auto" }}>
          {mode === "edit" && !editingIsDefault && (
            <button onClick={onRequestDelete} disabled={saving || deleting} style={{ padding: "9px 16px", borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: (saving || deleting) ? "not-allowed" : "pointer", fontFamily: FONT, border: "1px solid #F0C3BC", background: "#fff", color: "#B53B2E", display: "inline-flex", alignItems: "center", gap: 6, marginRight: "auto" }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
              Delete this {category}
            </button>
          )}
          {mode === "edit" && editingIsDefault && (
            <span style={{ fontSize: 11.5, color: C.ink, fontStyle: "italic", marginRight: "auto" }}>Default items cannot be deleted</span>
          )}
          <button onClick={onClose} disabled={saving || deleting} style={{ padding: "9px 16px", borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: (saving || deleting) ? "not-allowed" : "pointer", fontFamily: FONT, border: "1px solid " + C.line, background: "#fff", color: C.ink, marginLeft: mode === "add" ? "auto" : 0 }}>
            Cancel
          </button>
          <button onClick={onSave} disabled={saving || deleting} style={{ padding: "9px 16px", borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: (saving || deleting) ? "not-allowed" : "pointer", fontFamily: FONT, border: 0, background: (saving || deleting) ? "#94A0B2" : C.ink, color: "#fff", boxShadow: "0 1px 2px rgba(10,26,30,.1)" }}>
            {saving ? "Saving..." : (mode === "add" ? "Create " + category : "Save changes")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}